import { useEffect, useState } from 'react';
import { api } from '../services/api';

export const ModelSettings = () => {
  const [models, setModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState('qwen-max');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getModelSettings();
      setModels(data.models || []);
      setDefaultModel(data.default_model || 'qwen-max');
    } catch (e) {
      setError(e.message || 'Не удалось загрузить настройки моделей');
    } finally {
      setIsLoading(false);
    }
  };

  const updateModelFlag = (modelKey, flag, value) => {
    setSuccess(null);
    setModels((currentModels) => currentModels.map((model) => {
      if (model.key !== modelKey) return model;

      const nextModel = { ...model, [flag]: value };
      if (modelKey === defaultModel && (nextModel.is_blocked || nextModel.is_hidden)) {
        setDefaultModel('');
      }
      return nextModel;
    }));
  };

  const handleDefaultChange = (modelKey) => {
    const model = models.find((item) => item.key === modelKey);
    if (!model || model.is_blocked || model.is_hidden) return;

    setSuccess(null);
    setDefaultModel(modelKey);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);

    if (!defaultModel) {
      setError('Выберите доступную модель по умолчанию');
      return;
    }

    setIsSaving(true);
    try {
      const data = await api.updateModelSettings({
        default_model: defaultModel,
        models,
      });
      setModels(data.models || []);
      setDefaultModel(data.default_model || 'qwen-max');
      setSuccess('Настройки моделей сохранены');
    } catch (e) {
      setError(e.message || 'Не удалось сохранить настройки моделей');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Модели</h1>
        <p className="text-sm text-text-secondary mt-1">
          Управление моделью по умолчанию, блокировкой и скрытием моделей в пользовательском интерфейсе.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-300 rounded-xl px-4 py-3">
          {success}
        </div>
      )}

      <div className="bg-bg-secondary rounded-xl border border-border-color overflow-x-auto">
        <div className="min-w-[720px] grid grid-cols-[1fr_140px_140px_140px] gap-4 px-6 py-4 border-b border-border-color text-sm font-medium text-text-secondary">
          <div>Модель</div>
          <div className="text-center">По умолчанию</div>
          <div className="text-center">Заблокирована</div>
          <div className="text-center">Скрыта</div>
        </div>

        {isLoading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-14 bg-bg-tertiary rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="min-w-[720px] divide-y divide-border-color">
            {models.map((model) => {
              const isUnavailable = model.is_blocked || model.is_hidden;
              return (
                <div key={model.key} className="grid grid-cols-[1fr_140px_140px_140px] gap-4 px-6 py-4 items-center">
                  <div>
                    <div className="font-medium text-text-primary">{model.name}</div>
                    <div className="text-xs text-text-muted">{model.key}</div>
                  </div>

                  <label className="flex justify-center">
                    <input
                      type="radio"
                      name="defaultModel"
                      checked={defaultModel === model.key}
                      disabled={isUnavailable}
                      onChange={() => handleDefaultChange(model.key)}
                      className="h-4 w-4 accent-accent-primary disabled:opacity-40"
                    />
                  </label>

                  <label className="flex justify-center">
                    <input
                      type="checkbox"
                      checked={!!model.is_blocked}
                      onChange={(event) => updateModelFlag(model.key, 'is_blocked', event.target.checked)}
                      className="h-4 w-4 accent-accent-primary"
                    />
                  </label>

                  <label className="flex justify-center">
                    <input
                      type="checkbox"
                      checked={!!model.is_hidden}
                      onChange={(event) => updateModelFlag(model.key, 'is_hidden', event.target.checked)}
                      className="h-4 w-4 accent-accent-primary"
                    />
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        <button
          onClick={loadSettings}
          disabled={isLoading || isSaving}
          className="px-4 py-2 rounded-lg bg-bg-tertiary text-text-secondary hover:text-text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Сбросить
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading || isSaving}
          className="px-4 py-2 rounded-lg bg-accent-primary text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
        >
          {isSaving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
};
